'use client';

import { incrementTelemetry } from 'core-events/actions';
import Link from 'next/link';
import { useState, JSX, useCallback } from 'react';

import { Toast, TabsRoot, TabsList, TabsTrigger, TabsContent } from '@videoblocks/design-system';

import toCamelCase from '../../../utils/camelCase';
import CloneVoiceToSpeech from './_features/CloneVoiceToSpeech';
import TextToSpeech from './_features/TextToSpeech';
import VoiceChanger from './_features/VoiceChanger';

const tabs = [
    { label: 'Text to Speech', path: 'text-to-speech' },
    { label: 'Clone my Voice', path: 'clone-my-voice' },
    { label: 'Voice Changer', path: 'voice-changer' },
];

const VoiceoverContainer = ({ active }: { active: string }): JSX.Element => {
    const [error, setError] = useState<string | undefined>(undefined);
    const activeTab = tabs.find((t) => t.path === active) ?? tabs[0];

    const handleTabClick = useCallback((tabPath: string): void => {
        void incrementTelemetry(`ai.voiceover.tabClick.${toCamelCase(tabPath)}`);
    }, []);

    return (
        <>
            <TabsRoot defaultValue={activeTab.label} shape="square" outline activationMode="manual">
                <div className="flex justify-center items-start mb-6">
                    <TabsList className="bg-transparent rounded-lg p-1 flex-wrap justify-center h-auto">
                        {tabs.map((tab) => (
                            <TabsTrigger
                                key={tab.path}
                                value={tab.label}
                                asChild
                                className="flex-none data-[state=active]:bg-gray-900 data-[state=active]:font-semibold"
                            >
                                <Link
                                    href={`/voiceover/${tab.path}`}
                                    tabIndex={0}
                                    onClick={() => handleTabClick(tab.path)}
                                >
                                    {tab.label}
                                </Link>
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </div>
                <TabsContent value="Text to Speech" tabIndex={-1}>
                    <TextToSpeech />
                </TabsContent>
                <TabsContent value="Clone my Voice" tabIndex={-1}>
                    <CloneVoiceToSpeech setError={setError} />
                </TabsContent>
                <TabsContent value="Voice Changer" tabIndex={-1}>
                    <VoiceChanger />
                </TabsContent>
            </TabsRoot>
            <Toast
                open={!!error}
                onOpenChange={() => setError(undefined)}
                duration={5000}
                title={error}
                variant="error"
                viewportClassName="fixed bottom-[var(--spacing-12)] left-1/2 transform -translate-x-1/2 flex flex-col gap-[var(--spacing-2)] w-fit max-w-[calc(100vw-var(--spacing-12))] m-0 list-none z-[1050] outline-none"
            />
        </>
    );
};

export default VoiceoverContainer;
